import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { dispatchApi } from '../api/services';
import toast from 'react-hot-toast';
import { format, formatDistanceToNow } from 'date-fns';
import { Truck, Loader2 } from 'lucide-react';

/**
 * Pending tracking — shipments that have gone out on a dispatch batch but
 * don't have a tracking number yet. Kalpana's cue to chase the vendor.
 */
export default function PendingTracking() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dispatchApi.listPendingTracking()
      .then((res) => setRows(res.data.shipments || []))
      .catch((err) => toast.error(err.response?.data?.error || 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-semibold text-gray-900 mb-1">Pending tracking</h1>
      <p className="text-sm text-gray-500 mb-6">
        Shipments that have been dispatched to the vendor but are still waiting for a
        tracking number. The oldest ones are at the top — chase those first.
      </p>

      {loading ? (
        <div className="text-sm text-gray-500"><Loader2 className="w-4 h-4 animate-spin inline mr-1" />Loading…</div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-sm text-gray-500">
          <Truck className="w-8 h-8 mx-auto mb-2 text-gray-300" />
          Every dispatched shipment has a tracking number. Nice.
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="text-left px-3 py-2">Order</th>
                <th className="text-left px-3 py-2">Customer</th>
                <th className="text-left px-3 py-2">Vendor</th>
                <th className="text-left px-3 py-2">Dispatch batch</th>
                <th className="text-left px-3 py-2">Emailed to vendor</th>
                <th className="text-left px-3 py-2">Waiting</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((s) => (
                <tr key={s.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 font-mono text-xs">
                    <Link to={`/orders/${s.order_id}`} className="text-blue-600 hover:underline">
                      {s.order_id_external}
                    </Link>
                    {s.is_reship && <span className="ml-1 text-[10px] bg-purple-100 text-purple-700 px-1 rounded">reship</span>}
                  </td>
                  <td className="px-3 py-2">{s.customer_name}</td>
                  <td className="px-3 py-2 text-xs">{s.vendor_name || '—'}</td>
                  <td className="px-3 py-2 font-mono text-xs">
                    <Link to={`/dispatch/${s.dispatch_batch_id}`} className="text-blue-600 hover:underline">
                      {s.batch_number}
                    </Link>
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500">
                    {s.emailed_to_vendor_at
                      ? format(new Date(s.emailed_to_vendor_at), 'dd MMM HH:mm')
                      : <span className="text-amber-700">not emailed yet</span>}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500">
                    {s.emailed_to_vendor_at
                      ? formatDistanceToNow(new Date(s.emailed_to_vendor_at), { addSuffix: true })
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
